
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, MoreHorizontal, ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Form } from '@/components/ui/form';
import { CustomFormField } from '@/components/ui/custom-form-field';
import { useForm } from 'react-hook-form';

interface UsersListProps {
  searchQuery: string;
}

const UsersList = ({ searchQuery }: UsersListProps) => {
  // Mock data - would typically come from an API
  const users = [
    { id: '1', name: 'Admin User', email: 'admin', role: 'admin', status: 'active', lastLogin: '2023-12-10 14:30' },
    { id: '2', name: 'Editor User', email: 'editor', role: 'editor', status: 'active', lastLogin: '2023-12-09 10:15' },
    { id: '3', name: 'Viewer User', email: 'viewer', role: 'viewer', status: 'active', lastLogin: '2023-12-08 16:45' },
    { id: '4', name: 'John Smith', email: 'john', role: 'editor', status: 'inactive', lastLogin: '2023-11-20 09:30' },
    { id: '5', name: 'Sarah Johnson', email: 'sarah', role: 'viewer', status: 'active', lastLogin: '2023-12-05 11:20' },
  ];
  
  const filteredUsers = searchQuery 
    ? users.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.role.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;
  
  const form = useForm({
    defaultValues: {
      name: '',
      email: '',
      role: 'viewer',
      isActive: true
    }
  });
  
  const onSubmit = (data: any) => {
    console.log('User form submitted:', data);
    form.reset();
  };
  
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-200">
            <ShieldAlert className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      case 'editor':
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Editor
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200">
            <Shield className="h-3 w-3 mr-1" />
            Viewer
          </Badge>
        );
    }
  };
  
  return (
    <Card>
      <div className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    <Badge variant={user.status === 'active' ? 'default' : 'outline'}>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{user.lastLogin}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit User</DialogTitle>
                            <DialogDescription>
                              Make changes to the user's information.
                            </DialogDescription>
                          </DialogHeader>
                          
                          <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                              <CustomFormField
                                form={form}
                                name="name"
                                label="Full Name"
                                placeholder="e.g., John Smith"
                                required
                              />
                              
                              <CustomFormField
                                form={form}
                                name="email"
                                label="Username"
                                placeholder="e.g., john"
                                type="text"
                                required
                              />
                              
                              <div className="grid w-full max-w-sm items-center gap-1.5">
                                <label htmlFor="role" className="text-sm font-medium">Role</label>
                                <select
                                  id="role"
                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-foreground file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  {...form.register('role')}
                                >
                                  <option value="viewer">Viewer</option>
                                  <option value="editor">Editor</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <label htmlFor="isActive" className="text-sm font-medium">Active Status</label>
                                <Switch id="isActive" {...form.register('isActive')} />
                              </div>
                              
                              <DialogFooter>
                                <Button type="submit">Save Changes</Button>
                              </DialogFooter>
                            </form>
                          </Form>
                        </DialogContent>
                      </Dialog>
                      
                      <Button variant="ghost" size="icon">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6">
                  No users found matching your search criteria
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default UsersList;
